/**
 * Reward Catalog types matching the database schema.
 * These types mirror the reward_catalog table structure.
 */

// Enums for reward catalog
export type RewardStatus = 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
// Category and variant are now data-driven from backend - use string type
export type RewardCategory = string;
export type RewardVariant = string;
export type WorkflowType = 'FIXED_AMOUNT' | 'VARIABLE_AMOUNT';

// =============================================================================
// Redemption Configuration Types
// For configuring how rewards are fulfilled (manual, API, instant)
// =============================================================================

/**
 * Redemption type determines how the reward is fulfilled
 * - manual: External process handles fulfillment (e.g., Benepass CSV import)
 * - api: Notifies provider API synchronously during redemption
 * - instant: Marks COMPLETED immediately (digital delivery, no external call)
 */
export type RedemptionType = 'manual' | 'api' | 'instant';

/**
 * Retry configuration for API-based redemptions
 */
export interface RedemptionApiRetry {
  max_attempts: number;
  backoff_ms: number[];
}

/**
 * Success handling configuration for API responses
 */
export interface RedemptionApiSuccess {
  status_codes: number[];
  response_mapping?: Record<string, string>;
}

/**
 * Full API configuration for type="api" redemptions
 */
export interface RedemptionApiConfig {
  base_url: { nonprod: string; prod: string };
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH';
  headers?: Record<string, string>;
  payload?: Record<string, unknown>;
  retry?: RedemptionApiRetry;
  success?: RedemptionApiSuccess;
}

/**
 * Valid pre-redemption handler types.
 * Determines where pre-redemption form data is stored:
 * - "incomm": Routes to incomm_demo + incomm_purse tables
 * - absent/undefined: Default handler stores in context_json
 */
export type PreRedemptionHandlerType = 'incomm';

export interface PreRedemptionConfig {
  handler: PreRedemptionHandlerType;
}

export const PRE_REDEMPTION_HANDLER_OPTIONS = [
  { value: '', label: 'None (Default — context_json)' },
  { value: 'incomm', label: 'InComm (incomm_demo table)' },
] as const;

/**
 * Redemption configuration for a reward
 * Stored in reward_catalog.redemption_config JSONB column
 */
export interface RedemptionConfig {
  type: RedemptionType;
  provider: string;
  api?: RedemptionApiConfig;
  pre_redemption?: PreRedemptionConfig;
}

/**
 * Options for redemption type dropdown
 */
export const REDEMPTION_TYPE_OPTIONS = [
  { value: 'manual', label: 'Manual (External Process)' },
  { value: 'api', label: 'API (Notify Provider)' },
  { value: 'instant', label: 'Instant (Immediate Completion)' },
] as const;

/**
 * Get display label for redemption type
 */
export function getRedemptionTypeLabel(type: RedemptionType): string {
  const labels: Record<RedemptionType, string> = {
    manual: 'Manual',
    api: 'API',
    instant: 'Instant',
  };
  return labels[type] || type;
}

// =============================================================================
// Page-Centric Workflow Content Types
// Organization by the three workflow pages: Info Sheet, Amount Selection, Success
// =============================================================================

/**
 * Next step item for success screen
 */
export interface SuccessScreenNextStep {
  title: string;
  description: string;
}

/**
 * Metadata for workflow content (optional, used for tracking)
 */
export interface WorkflowContentMetadata {
  reward_id?: string;
  reward_key?: string;
  reward_variant?: string;
  generated_by?: string;
}

// =============================================================================
// Dynamic Action Section Types
// For configurable action buttons on the Success page
// =============================================================================

/**
 * Button variant - matches lore-core Button variants
 */
export type ActionButtonVariant = 'primary' | 'secondary' | 'neutral' | 'text' | 'accent' | 'link';

/**
 * Button type for dynamic action buttons
 */
export type ActionButtonType = 'external_link' | 'internal_navigation';

/**
 * Definition for a dynamic action button on the Success page
 */
export interface ActionButtonDefinition {
  id: string;
  type: ActionButtonType;
  label: string;
  /** Key in context_data to use as URL (e.g., "provider_url") */
  url_context_key: string;
  /** Optional: Static URL if not using context_data */
  static_url?: string;
  /** Button variant - matches lore-core Button */
  variant?: ActionButtonVariant;
}

/**
 * Definition for a dynamic action section on the Success page
 * Combines rich text content with action buttons.
 */
export interface ActionSectionDefinition {
  /** Rich text HTML content (rendered via RichContent) */
  content?: string;
  /** Action buttons to display */
  buttons: ActionButtonDefinition[];
}

// =============================================================================
// Dynamic Form Field Types
// For configurable form fields on the Amount Selection page
// =============================================================================

/**
 * Field type for dynamic form fields
 */
export type FormFieldType = 'radio' | 'text' | 'select' | 'checkbox_group' | 'email' | 'phone' | 'date';

/**
 * Width hint for form field layout
 */
export type FormFieldWidth = 'full' | 'half' | 'third' | 'quarter';

/**
 * Option for radio, select, or checkbox_group field types
 */
export interface FormFieldOption {
  value: string;
  label: string;
}

/**
 * Validation rules for form fields
 */
export interface FieldValidation {
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  customMessage?: string;
}

/**
 * Definition for a dynamic form field on the Amount Selection page
 *
 * Supports multiple field types:
 * - radio: Single-select radio button group (requires options)
 * - text: Free-form text input
 * - select: Dropdown select (requires options)
 * - checkbox_group: Multi-select checkboxes (requires options)
 * - email: Text input with email validation
 * - phone: Text input with phone number validation
 */
export interface FormFieldDefinition {
  id: string;
  type: FormFieldType;
  label: string;
  required: boolean;
  options?: FormFieldOption[];
  validation?: FieldValidation;
  placeholder?: string;
  helpText?: string;
  defaultValue?: string;
  width?: FormFieldWidth;
  /** Min date constraint for date fields. "today" resolves at render time, or use YYYY-MM-DD for static dates. */
  minDate?: string;
  /** Max date constraint for date fields. "today" resolves at render time, or use YYYY-MM-DD for static dates. */
  maxDate?: string;
  /** HTML inputMode hint for mobile keyboards (e.g., 'numeric' for zip codes, 'tel' for phone). */
  inputMode?: 'none' | 'text' | 'numeric' | 'tel' | 'email' | 'decimal';
}

/**
 * Layout variant for the info sheet page
 * - "default" (or absent): prose layout with illustration + "What is this?" + "How it works?"
 * - "stepper": vertical stepper with configurable steps (icon, title, description)
 */
export type InfoSheetLayout = 'default' | 'stepper';

/**
 * A single step in the stepper info sheet layout
 */
export interface InfoSheetStep {
  /** Icon filename in /icons/ (e.g., "info-step-smart-button.svg") */
  icon: string;
  /** Bold step title (e.g., "Redeem") */
  title: string;
  /** Step description, supports HTML for bold text via RichContent */
  description: string;
}

/**
 * Page-centric workflow content - matches database ui_content_json structure.
 *
 * Organization:
 * - info_sheet.* - Info Sheet page content
 * - amount_selection.* - Amount Selection page content
 * - success.* - Success page content
 *
 * Fields are accessed via flat dot notation keys (e.g., 'info_sheet.illustration').
 *
 * Template variables available:
 * - {{provider_name}} - Partner fulfillment provider name
 * - {{partner_dba}} - Partner's doing-business-as name
 * - {{actual_cost_in_points}} - Point cost from reward catalog
 * - {{actual_copay_in_dollars}} - Dollar copay amount
 * - {{monthly_points_remaining}} - User's remaining monthly points
 * - {{usd_value}} - Dollar value of points
 */
export interface WorkflowContent {
  // Workflow type (required)
  workflow_type: WorkflowType;

  // ===== Info Sheet Page (info_sheet.*) =====
  'info_sheet.layout'?: InfoSheetLayout;
  'info_sheet.steps'?: InfoSheetStep[];
  'info_sheet.show_arrows'?: boolean;
  'info_sheet.page_title'?: string;
  'info_sheet.illustration'?: string;
  'info_sheet.cta_label'?: string;
  'info_sheet.consent'?: string[];
  'info_sheet.what_is_this_title'?: string;
  'info_sheet.how_it_works_title'?: string;
  'info_sheet.what_is_this_content'?: string;
  'info_sheet.how_it_works_content_base'?: string;
  'info_sheet.how_it_works_content_minimum'?: string;
  'info_sheet.how_it_works_content_monthly_max'?: string;
  'info_sheet.how_it_works_content_yearly_max'?: string;
  'info_sheet.how_it_works_content_provider'?: string;

  // ===== Amount Selection Page (amount_selection.*) =====
  'amount_selection.page_title'?: string;
  'amount_selection.input_label'?: string;
  'amount_selection.input_placeholder'?: string;
  'amount_selection.help_text_base'?: string;
  'amount_selection.help_text_minimum'?: string;
  'amount_selection.help_text_monthly_max'?: string;
  'amount_selection.help_text_yearly_max'?: string;
  'amount_selection.validation_required'?: string;
  'amount_selection.validation_min'?: string;
  'amount_selection.validation_max_base'?: string;
  'amount_selection.validation_max_monthly_limit'?: string;
  'amount_selection.validation_max_annual_limit'?: string;
  'amount_selection.instructions_title'?: string;
  'amount_selection.instructions_content'?: string;
  'amount_selection.consent'?: string[];
  'amount_selection.disclaimer'?: string;
  'amount_selection.fixed_amount_title'?: string;
  'amount_selection.fixed_amount_label'?: string;
  'amount_selection.fixed_amount_value'?: string;
  'amount_selection.fixed_remaining_label'?: string;
  'amount_selection.fixed_description'?: string;

  // Dynamic form fields (optional, for rewards requiring additional user input)
  'amount_selection.form_fields'?: FormFieldDefinition[];

  // ===== Success Page (success.*) =====
  'success.layout'?: 'default' | 'card';
  'success.title'?: string;
  'success.subtitle'?: string;
  'success.main_message'?: string;
  'success.illustration_icon'?: string;
  'success.card_description'?: string;
  'success.cta_text'?: string;
  'success.summary_redeemed_label'?: string;
  'success.summary_redeemed_value'?: string;
  'success.summary_total_points_label'?: string;
  'success.summary_total_points_value'?: string;
  'success.alert_heading'?: string;
  'success.alert_message'?: string;
  'success.next_steps'?: string;

  // Dynamic action section (optional, for rewards with provider URLs)
  'success.action_section'?: ActionSectionDefinition;

  // Footer section (optional, replaces simple CTA when present)
  'success.footer_section'?: ActionSectionDefinition;

  // ===== Pre-Redemption Page (pre_redemption.*) =====
  // For rewards requiring data collection before redemption (e.g., shipping address)
  'pre_redemption.page_title'?: string;
  'pre_redemption.illustration'?: string;
  'pre_redemption.description'?: string;
  'pre_redemption.cta_label'?: string;
  'pre_redemption.success_layout'?: 'page' | 'sheet';
  'pre_redemption.success_message'?: string;
  'pre_redemption.info_sheet_description'?: string; // Shows on info sheet after submission
  'pre_redemption.form_fields'?: FormFieldDefinition[];

  // ===== Unavailable Info Sheet (unavailable_info_sheet.*) =====
  // Shown instead of the normal info sheet when can_redeem=false and these fields are configured
  'unavailable_info_sheet.layout'?: InfoSheetLayout;
  'unavailable_info_sheet.steps'?: InfoSheetStep[];
  'unavailable_info_sheet.show_arrows'?: boolean;
  'unavailable_info_sheet.page_title'?: string;
  'unavailable_info_sheet.alert_title'?: string;
  'unavailable_info_sheet.alert_body'?: string;
  'unavailable_info_sheet.cta_label'?: string;
  'unavailable_info_sheet.cta_label_submitted'?: string;
  'unavailable_info_sheet.cta_route'?: string;
  'unavailable_info_sheet.cta_route_submitted'?: string;

  // ===== Offer Card (offer_card.*) =====
  // Status labels on the offer card (available vs unavailable for redemption)
  'offer_card.available_label'?: string;
  'offer_card.unavailable_label'?: string;

  // --- Metadata (optional) ---
  _metadata?: WorkflowContentMetadata;

  // Allow additional flat keys for extensibility
  [key: string]: unknown;
}

/**
 * All known flat field paths in WorkflowContent.
 * Used for field pickers, content override editors, etc.
 */
export const WORKFLOW_CONTENT_FIELD_PATHS = {
  // Info Sheet Page
  infoSheet: [
    'info_sheet.layout',
    'info_sheet.steps',
    'info_sheet.show_arrows',
    'info_sheet.page_title',
    'info_sheet.illustration',
    'info_sheet.cta_label',
    'info_sheet.what_is_this_title',
    'info_sheet.how_it_works_title',
    'info_sheet.what_is_this_content',
    'info_sheet.how_it_works_content_base',
    'info_sheet.how_it_works_content_minimum',
    'info_sheet.how_it_works_content_monthly_max',
    'info_sheet.how_it_works_content_yearly_max',
    'info_sheet.how_it_works_content_provider',
    'info_sheet.consent',
  ],
  // Amount Selection Page
  amountSelection: [
    'amount_selection.page_title',
    'amount_selection.input_label',
    'amount_selection.input_placeholder',
    'amount_selection.help_text_base',
    'amount_selection.help_text_minimum',
    'amount_selection.help_text_monthly_max',
    'amount_selection.help_text_yearly_max',
    'amount_selection.validation_required',
    'amount_selection.validation_min',
    'amount_selection.validation_max_base',
    'amount_selection.validation_max_monthly_limit',
    'amount_selection.validation_max_annual_limit',
    'amount_selection.instructions_title',
    'amount_selection.instructions_content',
    'amount_selection.consent',
    'amount_selection.disclaimer',
    'amount_selection.fixed_amount_title',
    'amount_selection.fixed_amount_label',
    'amount_selection.fixed_amount_value',
    'amount_selection.fixed_remaining_label',
    'amount_selection.fixed_description',
    'amount_selection.form_fields',
  ],
  // Success Page
  success: [
    'success.page_title',
    'success.layout',
    'success.title',
    'success.subtitle',
    'success.main_message',
    'success.illustration_icon',
    'success.card_description',
    'success.cta_text',
    'success.summary_redeemed_label',
    'success.summary_redeemed_value',
    'success.summary_total_points_label',
    'success.summary_total_points_value',
    'success.alert_heading',
    'success.alert_message',
    'success.next_steps',
    'success.action_section',
    'success.footer_section',
  ],
  // Pre-Redemption Page
  preRedemption: [
    'pre_redemption.page_title',
    'pre_redemption.illustration',
    'pre_redemption.description',
    'pre_redemption.cta_label',
    'pre_redemption.post_submit_available',
    'pre_redemption.post_submit_blocking_reason',
    'pre_redemption.info_sheet_description',
    'pre_redemption.form_fields',
    'pre_redemption.success_layout',
    'pre_redemption.success_page_title',
    'pre_redemption.success_title',
    'pre_redemption.success_illustration',
    'pre_redemption.success_description',
    'pre_redemption.success_cta_label',
  ],
  // Unavailable Info Sheet
  unavailableInfoSheet: [
    'unavailable_info_sheet.layout',
    'unavailable_info_sheet.steps',
    'unavailable_info_sheet.show_arrows',
    'unavailable_info_sheet.page_title',
    'unavailable_info_sheet.alert_title',
    'unavailable_info_sheet.alert_body',
    'unavailable_info_sheet.cta_label',
    'unavailable_info_sheet.cta_label_submitted',
    'unavailable_info_sheet.cta_route',
    'unavailable_info_sheet.cta_route_submitted',
  ],
  // Offer Card
  offerCard: ['offer_card.available_label', 'offer_card.unavailable_label'],
} as const;

/**
 * All flat field paths as a flat array
 */
export const ALL_WORKFLOW_CONTENT_FIELD_PATHS = [
  ...WORKFLOW_CONTENT_FIELD_PATHS.infoSheet,
  ...WORKFLOW_CONTENT_FIELD_PATHS.amountSelection,
  ...WORKFLOW_CONTENT_FIELD_PATHS.success,
  ...WORKFLOW_CONTENT_FIELD_PATHS.preRedemption,
  ...WORKFLOW_CONTENT_FIELD_PATHS.unavailableInfoSheet,
  ...WORKFLOW_CONTENT_FIELD_PATHS.offerCard,
] as const;

/**
 * Template variables available for use in content strings
 */
export const TEMPLATE_VARIABLES = [
  { variable: '{{reward_name}}', description: 'The display name of the reward' },
  {
    variable: '{{provider_name}}',
    description: 'Partner fulfillment provider name (e.g., Benepass)',
  },
  { variable: '{{partner_dba}}', description: "Partner's doing-business-as name" },
  { variable: '{{actual_cost_in_points}}', description: 'Point cost from reward catalog' },
  { variable: '{{actual_copay_in_dollars}}', description: 'Dollar copay amount' },
  { variable: '{{monthly_points_remaining}}', description: "User's remaining monthly points" },
  { variable: '{{usd_value}}', description: 'Dollar value of points' },
  { variable: '{{actual_monthly_limit}}', description: 'Monthly limit in dollars' },
  { variable: '{{actual_annual_limit}}', description: 'Annual limit in dollars' },
  { variable: '{{monthly_limit_points}}', description: 'Monthly limit in points' },
  { variable: '{{annual_limit_points}}', description: 'Annual limit in points' },
  { variable: '{{points_per_dollar}}', description: 'Points per dollar conversion rate' },
  { variable: '{{first_name}}', description: "User's first name from verification profile" },
  { variable: '{{last_name}}', description: "User's last name from verification profile" },
  { variable: '{{birth_date}}', description: "User's date of birth from verification profile" },
  { variable: '{{email}}', description: "User's email from verification profile" },
] as const;

// =============================================================================
// Reward Configuration Types
// Per-variant reward config stored in reward_catalog.reward_config JSONB
// =============================================================================

export type LimitType = 'count' | 'dollar' | 'unlimited';

export const LIMIT_TYPE_OPTIONS = [
  { value: 'unlimited', label: 'Unlimited' },
  { value: 'dollar', label: 'Dollar-based' },
  { value: 'count', label: 'Count-based' },
] as const;

export function getLimitTypeLabel(limitType: LimitType): string {
  const labels: Record<LimitType, string> = {
    unlimited: 'Unlimited',
    dollar: 'Dollar',
    count: 'Count',
  };
  return labels[limitType] || limitType;
}

/**
 * Generic pool configuration for shared spending/redemption limits across reward variants.
 * All variants with the same pool_id share aggregate limits.
 */
export interface LimitPoolConfig {
  pool_id: string;
  limit_usd?: number | null;
  limit_count?: number | null;
}

/**
 * Per-variant reward configuration.
 * Controls limits, availability, pool membership, and completion points.
 */
export interface RewardConfig {
  limit_type: LimitType;
  monthly_limit_usd?: number | null;
  annual_limit_usd?: number | null;
  monthly_limit_count?: number | null;
  annual_limit_count?: number | null;
  available?: boolean;
  availability_message?: string | null;
  show_message_on_card?: boolean;
  collect_pre_redemption_data?: boolean;
  annual_pool?: LimitPoolConfig | null;
  monthly_pool?: LimitPoolConfig | null;
  completion_points?: number | null;
}

/**
 * Pool member info returned from the pools endpoint.
 */
export interface PoolMemberInfo {
  reward_id: string;
  reward_key: string;
  reward_variant: string;
  reward_name: string;
}

/**
 * Aggregated pool info returned from GET /admin/catalog/pools.
 */
export interface PoolInfo {
  pool_id: string;
  limit_usd: number | null;
  limit_count: number | null;
  limit_type: LimitType;
  member_count: number;
  members: PoolMemberInfo[];
}

export interface PoolListResponse {
  annual_pools: PoolInfo[];
  monthly_pools: PoolInfo[];
}

// Main reward response matching database columns
export interface RewardResponse {
  reward_id: string; // UUID
  reward_key: string;
  reward_name: string;
  ux_reward_title: string;
  ux_reward_subtitle: string | null;
  ux_reward_description: string | null;
  ux_image_url: string | null;
  category: RewardCategory;
  status: RewardStatus;
  point_cost: number;
  dollar_value: number | string | null; // Can be string from Decimal serialization
  out_of_pocket_cost: number | string | null; // Can be string from Decimal serialization
  /** Workflow content using flat dot notation structure */
  ui_content_json: WorkflowContent | null;
  /** Redemption configuration (manual, api, instant) */
  redemption_config: RedemptionConfig | null;
  /** Per-variant reward configuration (limits, availability, pool, completion points) */
  reward_config: RewardConfig | null;
  reward_variant: RewardVariant;
  /** Provider name (auto-synced from redemption_config.provider if not set) */
  provider_name: string | null;
  created_at: string; // ISO datetime
  updated_at: string; // ISO datetime
  created_by: string;
}

// Query parameters for filtering rewards
export interface RewardQueryParams {
  category?: RewardCategory;
  status?: RewardStatus;
  reward_variant?: RewardVariant;
  search?: string; // searches reward_key, reward_name, ux_reward_title
  limit?: number;
  offset?: number;
}

// Paginated list response
export interface RewardListResponse {
  rewards: RewardResponse[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

// Create reward request
export interface CreateRewardRequest {
  reward_key: string;
  reward_name: string;
  ux_reward_title: string;
  ux_reward_subtitle?: string | null;
  ux_reward_description?: string | null;
  ux_image_url?: string | null;
  category: RewardCategory;
  status: RewardStatus;
  point_cost: number;
  dollar_value?: number | null;
  out_of_pocket_cost?: number | null;
  /** Workflow content using flat dot notation structure */
  ui_content_json?: WorkflowContent | null;
  /** Redemption configuration (manual, api, instant) */
  redemption_config?: RedemptionConfig | null;
  /** Per-variant reward configuration (limits, availability, pool, completion points) */
  reward_config?: RewardConfig | null;
  reward_variant: RewardVariant;
  /** Provider name (auto-synced from redemption_config.provider if not set) */
  provider_name?: string | null;
}

// Update reward request (all fields optional except id)
export interface UpdateRewardRequest {
  reward_key?: string;
  reward_name?: string;
  ux_reward_title?: string;
  ux_reward_subtitle?: string | null;
  ux_reward_description?: string | null;
  ux_image_url?: string | null;
  category?: RewardCategory;
  status?: RewardStatus;
  point_cost?: number;
  dollar_value?: number | null;
  out_of_pocket_cost?: number | null;
  /** Workflow content using flat dot notation structure */
  ui_content_json?: WorkflowContent | null;
  /** Redemption configuration (manual, api, instant) */
  redemption_config?: RedemptionConfig | null;
  /** Per-variant reward configuration (limits, availability, pool, completion points) */
  reward_config?: RewardConfig | null;
  reward_variant?: RewardVariant;
  /** Provider name (auto-synced from redemption_config.provider if not set) */
  provider_name?: string | null;
}

// Category options for filter dropdown
export interface CategoryOption {
  value: RewardCategory | '';
  label: string;
}

// CATEGORY_OPTIONS is now fetched dynamically from backend
// Use getCategoryOptions() from rewardService or useRewardMetadata hook
export const CATEGORY_OPTIONS: CategoryOption[] = []; // Deprecated - use dynamic data

// Status options for filter dropdown
export interface StatusOption {
  value: RewardStatus | '';
  label: string;
}

export const STATUS_OPTIONS: StatusOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ARCHIVED', label: 'Archived' },
];

// Variant options for filter dropdown
export interface VariantOption {
  value: RewardVariant | '';
  label: string;
}

// VARIANT_OPTIONS is now fetched dynamically from backend
// Use getVariantOptions() from rewardService or useRewardMetadata hook
export const VARIANT_OPTIONS: VariantOption[] = []; // Deprecated - use dynamic data

// Helper functions

export function getCategoryLabel(category: RewardCategory): string {
  // Format category string for display (capitalize words)
  return category
    .split(/[\s-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getCategoryColor(category: RewardCategory): string {
  // Use hash-based color assignment for dynamic categories
  const colors = ['info', 'warning', 'success', 'primary', 'secondary', 'danger'];
  const hash = category.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return colors[hash % colors.length] || 'default';
}

export function getStatusColor(status: RewardStatus): string {
  const colors: Record<RewardStatus, string> = {
    ACTIVE: 'success',
    DRAFT: 'default',
    ARCHIVED: 'danger',
  };
  return colors[status] || 'default';
}

export function getVariantLabel(variant: RewardVariant): string {
  // Format variant string for display (capitalize words, replace hyphens with spaces)
  return variant
    .split(/[\s-]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getWorkflowTypeLabel(workflowType: WorkflowType): string {
  const labels: Record<WorkflowType, string> = {
    FIXED_AMOUNT: 'Fixed Amount',
    VARIABLE_AMOUNT: 'Variable Amount',
  };
  return labels[workflowType] || workflowType;
}

export function formatPointCost(points: number): string {
  return points.toLocaleString() + ' pts';
}

export function formatDollarValue(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '—';
  // Handle Decimal types from backend (serialized as strings)
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '—';
  return '$' + numValue.toFixed(2);
}
